/* eslint-disable consistent-return */
/* eslint-disable max-len */
import { observable, makeObservable } from 'mobx'
import * as R from 'ramda'
import i18n from 'i18next'

class QListSearchStore {
    clients = []

    divisions = []

    portfolios = []

    customCategories = []

    @observable filteredClients = []

    @observable filteredDivisions = []

    @observable filteredPortfolios = []

    @observable filteredCustomCategories = []

    filteredCustomCategoryOptions = []

    @observable questionDataSet = []

    selectedQuestionTitle = ''

    constructor(store) {
        makeObservable(this)
        this.appStore = store
    }

    init = async () => {
        const [divisions, portfolios, customCategories, clients] = await Promise.all([
            this.getDivisions(),
            this.getPortfolios(),
            this.getCustomCategories(),
            this.getClient(),
        ])

        this.clients = clients
        this.divisions = divisions
        this.portfolios = portfolios
        this.customCategories = customCategories
        this.filteredClients = clients
        this.filteredDivisions = divisions
        this.filteredPortfolios = portfolios
        this.filteredCustomCategories = customCategories

        this.filteredCustomCategoryOptions = this.groupedCategories
        this.setQuestionItemValues()
        this.setPanels()
    }

    setQuestionItemValues = () => {
        let clientObj = {}
        let divisionObj = {}
        let portfolioObj = {}
        const questionDataSet = []

        if (this.filteredClients.length > 0) {
            clientObj = {
                client: this.filteredClients,
            }
            questionDataSet.push(clientObj)
        }
        if (this.filteredDivisions.length > 0) {
            divisionObj = {
                divisions: this.filteredDivisions,
            }
            questionDataSet.push(divisionObj)
        }
        if (this.filteredPortfolios.length > 0) {
            portfolioObj = {
                portfolios: this.filteredPortfolios,
            }
            questionDataSet.push(portfolioObj)
        }
        if (Object.keys(this.filteredCustomCategories).length > 0) {
            Object.keys(this.filteredCustomCategories).forEach(
                (id) => (questionDataSet.push({ [id]: this.filteredCustomCategories[id] })),
            )
        }

        this.questionDataSet = questionDataSet
    }

    setPanels = () => {
        const { appStore } = this
        const { filteredCustomCategories } = this
        const role = R.path(['userData', 'role'], appStore)
        const isMultiDivision = R.path(['userData', 'flags', 'isMultiDivision'], appStore)
        this.panels = {
            client: {
                title: i18n.t('mandatoryQuestions.gettingStartedPanel.categoryItem.header.client'),
                description: i18n.t('mandatoryQuestions.gettingStartedPanel.categoryItem.content.client'),
                isVisible: () => role <= 110,
            },
            divisions: {
                title: i18n.t('mandatoryQuestions.gettingStartedPanel.categoryItem.header.divisions'),
                description: i18n.t('mandatoryQuestions.gettingStartedPanel.categoryItem.content.divisions'),
                isVisible: () => (isMultiDivision && role <= 120),
            },
            portfolios: {
                title: i18n.t('mandatoryQuestions.gettingStartedPanel.categoryItem.header.portfolios'),
                description: i18n.t('mandatoryQuestions.gettingStartedPanel.categoryItem.content.portfolios'),
                isVisible: () => true,
            },
        }

        Object.keys(filteredCustomCategories).forEach((vocabularyId) => {
            this.panels[vocabularyId] = {
                name: filteredCustomCategories[vocabularyId][0].vocabularyName,
                isVisible: () => role <= 110,
                options: filteredCustomCategories[vocabularyId],
                render: (state) => (state.map(this.renderCardItems)),
            }
        })
    }

    getClient = async () => {
        const { appStore } = this
        const name = R.path(['userData', 'clientName'], appStore)
        const id = R.path(['tokenData', 'clientId'], appStore)
        const oid = 3

        const clients = []

        clients.push({
            name,
            id,
            oid,
        })

        return clients
    }

    getDivisions = async () => {
        const { appStore } = this
        const role = R.path(['userData', 'role'], appStore)
        const isMultiDivision = R.path(['userData', 'flags', 'isMultiDivision'], appStore)

        let divisions = []

        if (role <= 120 && isMultiDivision) {
            const divisionResult = await appStore.http.get('/division')
            divisions = divisionResult.map(({ divisionId, name }) => ({
                id: divisionId,
                name,
                oid: 4,
            }))
        }

        return divisions
    }

    getPortfolios = async () => {
        const { appStore } = this

        const portfolioResopnse = await appStore.http.get('/portfolio')
        return portfolioResopnse.map(({ portfolioId, name }) => ({
            id: portfolioId,
            name,
            oid: 5,
        }))
    }

    getCustomCategories = async () => {
        const { appStore } = this
        let categories = []
        const role = R.path(['userData', 'role'], appStore)

        if (role > 110) return categories

        const categoryResponse = await appStore.http.get('/taxonomy/term')

        categories = R.groupBy(R.prop('vocabularyId'), categoryResponse.map((category) => ({
            id: category.termId,
            name: category.name,
            vocabularyId: category.vocabularyId,
            vocabularyName: category.vocabularyName,
            oid: 8,
        })))
        this.groupedCategories = categories
        return categories
    }

    filterCustomCategories = (obj, searchString) => {
        const res = {}
        Object.keys(obj).forEach((id) => {
            const filtered = obj[id].filter((p) => `${p.id}${p.name.toLowerCase()}`.includes(searchString.toLowerCase()))
            if (filtered.length === 0) return
            res[id] = filtered
        })
        return res
    }

    resetFilters = () => {
        this.filteredClients = this.clients
        this.filteredDivisions = this.divisions
        this.filteredPortfolios = this.portfolios
        this.filteredCustomCategories = this.customCategories
        this.setQuestionItemValues()
    }

    search = () => {
        const { searchStr } = this.searchModule
        this.searchString = searchStr

        let filteredClients = []
        let filteredDivisions = []
        let filteredPortfolios = []
        let filteredCustomCategories = []

        if (searchStr === '') {
            return this.resetFilters()
        }
        filteredClients = this.clients.filter((c) => `${c.id}${c.name.toLowerCase()}`.includes(searchStr.toLowerCase()))
        filteredDivisions = this.divisions.filter((d) => `${d.id}${d.name.toLowerCase()}`.includes(searchStr.toLowerCase()))
        filteredPortfolios = this.portfolios.filter((p) => `${p.id}${p.name.toLowerCase()}`.includes(searchStr.toLowerCase()))

        filteredCustomCategories = this.filterCustomCategories(this.customCategories, searchStr)

        this.filteredClients = filteredClients
        this.filteredDivisions = filteredDivisions
        this.filteredPortfolios = filteredPortfolios
        this.filteredCustomCategories = filteredCustomCategories
        this.setQuestionItemValues()
    }
}

export default QListSearchStore
